// The backend guards against an end at or before the start — appointments in
// scheduling/models/appointment.py (validate_appointment_time_range), schedule
// rows in ScheduleSerializer.validate_datetimes — but the message arrives as a
// non_field_error with the generic "invalid" code, so the shared useErrors
// whitelist drops it and the save falls back to a generic toast. Recognize
// those specific messages and surface patient-facing copy. Map known messages
// here one by one rather than letting every "invalid" through: most are worded
// for developers (field names, ids).
const APPOINTMENT_SAVE_ERROR_COPY: { match: string; display: string }[] = [
  {
    match: "end_moment must be equal or greater than start_moment",
    display: "End time must be after the start time."
  },
  {
    // A clinic or personal event, work shift or dedicated slot (DEV-2753, ENG-2313).
    match: "end_time must be greater than start_time",
    display: "End time must be after the start time."
  }
];

// ENG-2582: AssignedTagSerializer refuses a tag the practice can no longer assign
// (`_validate_tag_is_assignable`: deleted, unshared, or a global hidden by
// `hide_global_tags`), and a reschedule carries the source appointment's tags as
// creates. The refusal comes back per sent tag, `{}` for the ones that passed, so
// the position in `assigned_tags.create` is the position in what the form sent.
export const getRejectedTagCreateIndexes = (response: any): number[] => {
  const creates = response?.error?.data?.assigned_tags?.create;
  if (!Array.isArray(creates)) return [];

  return creates.flatMap((entry, index) =>
    Array.isArray(entry?.tag) && entry.tag.length ? [index] : []
  );
};

/** The sent tags the backend refused, in the order they were sent. */
export const getRefusedTagCreates = <T>(response: any, sentTags: T[]): T[] =>
  getRejectedTagCreateIndexes(response).flatMap((index) =>
    index < sentTags.length ? [sentTags[index]] : []
  );

// What the appointment form says after it has taken the refused tags off, so the
// user knows which ones went and that the next Save books without them.
export const getRemovedUnavailableTagsMessage = (tagNames: string[]) => {
  const quoted = tagNames.map((name) => `“${name}”`);
  const list =
    quoted.length > 1
      ? `${quoted.slice(0, -1).join(", ")} and ${quoted[quoted.length - 1]}`
      : quoted[0];
  return quoted.length > 1
    ? `${list} are no longer available to your practice, so they were taken off this appointment. Click Save again.`
    : `${list} is no longer available to your practice, so it was taken off this appointment. Click Save again.`;
};

export const getFriendlyAppointmentSaveError = (
  response: any
): string | undefined => {
  if (response?.error?.data?.code === "appointment_stale_update") {
    return "This appointment was changed by someone else while you were editing it. Please close and reopen it to see the latest, then reapply your change.";
  }

  if (getRejectedTagCreateIndexes(response).length) {
    return "A tag on this appointment is no longer available to your practice. Remove it and save again.";
  }

  const nonFieldErrors = response?.error?.data?.non_field_errors;
  if (!Array.isArray(nonFieldErrors)) return undefined;

  for (const nonFieldError of nonFieldErrors) {
    const message = String(nonFieldError?.message ?? "");
    const mapped = APPOINTMENT_SAVE_ERROR_COPY.find(({ match }) =>
      message.includes(match)
    );
    if (mapped) return mapped.display;
  }

  return undefined;
};
